import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getWarriorOptions, updateWarrior } from '../../services/questService';
import { MdArrowForward, MdArrowBack, MdCheck } from 'react-icons/md';

const RARITY_ICONS = {
  'The Phoenix':'🦅','The Tiger':'🐯','The Storm':'⛈️','The Dragon':'🐉',
  'The Lightning':'⚡','The Falcon':'🦅','The Wolf':'🐺','The Eagle':'🦅',
  'The Panther':'🐆','The Cobra':'🐍','The Hawk':'🦅','The Bear':'🐻',
};

function WarriorSetupModal({ userName, onComplete }) {
  const queryClient = useQueryClient();
  const [step,         setStep]  = useState(1);
  const [chosenTitle,  setTitle] = useState(null);
  const [chosenAvatar, setAva]   = useState(1);

  const { data } = useQuery({
    queryKey: ['warrior-options'],
    queryFn:  () => getWarriorOptions().then(r => r.data),
  });

  const titles  = data?.titles  || [];
  const avatars = data?.avatars || [];

  const mutation = useMutation({
    mutationFn: () => updateWarrior({ warriorTitle: chosenTitle, avatarStyle: chosenAvatar }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student-me'] });
      queryClient.invalidateQueries({ queryKey: ['my-gamification'] });
      toast.success(`Welcome, ${firstName} ${chosenTitle}! 🔥`);
      onComplete?.();
    },
    onError: () => toast.error('Failed to save warrior identity'),
  });

  const firstName = userName?.split(' ')[0] || userName || 'Warrior';

  // Determine current avatar emoji
  const currentAvatarEmoji = avatars.find(a => a.style === chosenAvatar)?.emoji || '🥷';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header gradient */}
        <div className="bg-gradient-to-r from-primary to-red-700 px-6 py-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-white/70 text-xs font-semibold uppercase tracking-widest">
              Step {step} of 3
            </p>
            <div className="flex gap-1">
              {[1,2,3].map(s => (
                <div key={s} className={`w-6 h-1.5 rounded-full ${s <= step ? 'bg-white' : 'bg-white/30'}`} />
              ))}
            </div>
          </div>
          <h2 className="text-white font-black text-xl">
            {step === 1 && 'Choose Your Warrior Title'}
            {step === 2 && 'Pick Your Avatar'}
            {step === 3 && 'Your Warrior Identity'}
          </h2>
          <p className="text-white/70 text-sm mt-1">
            {step === 1 && 'This title will appear next to your name in battles and the leaderboard.'}
            {step === 2 && 'Choose the avatar that represents your spirit.'}
            {step === 3 && 'This is who you are. Own it.'}
          </p>
        </div>

        <div className="p-6">
          {/* Step 1 — Choose Title */}
          {step === 1 && (
            <div className="grid grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
              {titles.map(({ title, icon }) => (
                <button
                  key={title}
                  onClick={() => setTitle(title)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-2xl border-2 transition-all ${
                    chosenTitle === title
                      ? 'border-primary bg-red-50 scale-105 shadow-md'
                      : 'border-gray-100 hover:border-primary/40 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-3xl">{icon}</span>
                  <p className={`text-xs font-semibold text-center leading-tight ${chosenTitle === title ? 'text-primary' : 'text-secondary'}`}>
                    {title}
                  </p>
                  {chosenTitle === title && <MdCheck className="text-primary text-sm" />}
                </button>
              ))}
            </div>
          )}

          {/* Step 2 — Choose Avatar */}
          {step === 2 && (
            <div className="grid grid-cols-4 gap-3">
              {avatars.map(({ style, emoji }) => (
                <button
                  key={style}
                  onClick={() => setAva(style)}
                  className={`flex flex-col items-center gap-1 p-3 rounded-2xl border-2 transition-all ${
                    chosenAvatar === style
                      ? 'border-primary bg-red-50 scale-110 shadow-md'
                      : 'border-gray-100 hover:border-primary/40'
                  }`}
                >
                  <span className="text-4xl">{emoji}</span>
                  <p className={`text-xs font-semibold ${chosenAvatar === style ? 'text-primary' : 'text-gray-400'}`}>
                    #{style}
                  </p>
                </button>
              ))}
            </div>
          )}

          {/* Step 3 — Preview */}
          {step === 3 && (
            <div className="flex flex-col items-center py-4 gap-4">
              <div className="w-24 h-24 bg-gradient-to-br from-primary to-red-700 rounded-full flex items-center justify-center text-5xl shadow-xl animate-bounce">
                {currentAvatarEmoji}
              </div>
              <div className="text-center">
                <p className="text-3xl font-black text-secondary">{firstName}</p>
                <p className="text-xl font-bold text-primary">{chosenTitle || 'The Warrior'}</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-4 w-full text-center">
                <p className="text-xs text-gray-400 mb-1">Your warrior name</p>
                <p className="font-bold text-secondary text-lg">
                  {firstName} {chosenTitle || 'The Warrior'}
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  You can change your warrior identity at any time from your profile.
                </p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-5 pt-4 border-t border-gray-100">
            {step > 1 ? (
              <button
                onClick={() => setStep(s => s - 1)}
                className="flex items-center gap-1.5 text-gray-400 hover:text-secondary text-sm font-medium transition-colors"
              >
                <MdArrowBack /> Back
              </button>
            ) : (
              <div />
            )}

            {step < 3 ? (
              <button
                onClick={() => setStep(s => s + 1)}
                disabled={step === 1 && !chosenTitle}
                className={`flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  step === 1 && !chosenTitle
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-primary text-white hover:bg-red-700 shadow-md shadow-primary/25'
                }`}
              >
                Next <MdArrowForward />
              </button>
            ) : (
              <button
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending}
                className="flex items-center gap-1.5 px-6 py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:bg-red-700 shadow-md shadow-primary/25 transition-all"
              >
                {mutation.isPending ? 'Saving…' : '🔥 Begin My Journey'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WarriorSetupModal;
